import { useState, useEffect } from "react";

export default function AuthPage({ modeProp = "signin", onLogin, onRegister, onClose }) {
  // mode can be: 'signin', 'signup', or 'otp'
  const [mode, setMode] = useState(modeProp);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // 🌟 State for the 2-minute countdown (120 seconds)
  const [timer, setTimer] = useState(0);

  // 🌟 Effect running a countdown interval when mode shifts to "otp"
  useEffect(() => {
    let intervalId;
    if (mode === "otp" && timer > 0) {
      intervalId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [mode, timer]);

  // Helper to format remaining seconds into mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  async function handleResendOtp() {
    setLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/send-otp/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phone_number: phoneNumber }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to resend verification email.");

      setSuccessMessage("New verification code dispatched!");
      setTimer(120); // 🌟 Reset the lock for another 2 minutes
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      // --- SIGN IN FLOW ---
      if (mode === "signin") {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/login/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Invalid credentials");

        onLogin && onLogin(data);
        onClose && onClose();
      }
      // --- SIGN UP STEP 1: REQUEST OTP ---
      else if (mode === "signup") {
        if (password !== password2) {
          throw new Error("Passwords do not match.");
        }

        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/send-otp/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone_number: phoneNumber }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to send verification email.");

        setSuccessMessage("Verification code dispatched! Please check your email inbox.");
        setMode("otp");
        setTimer(120); // 🌟 Initialize countdown to 120 seconds (2 mins)
      }
      // --- SIGN UP STEP 2: VERIFY OTP & COMPLETE REGISTRATION ---
      else if (mode === "otp") {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/register/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            otp,
            password,
            password2,
            phone_number: phoneNumber,
            first_name: firstName,
            last_name: lastName,
          }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Registration validation failed.");

        setOtp("");
        setPassword("");
        setPassword2("");


        onRegister && onRegister(data);
        onClose && onClose();

        setSuccessMessage("User registered successfully! Please sign in with your credentials.");
        setMode("signin");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-[#F2F1EC] min-h-screen flex items-center justify-center p-4 font-sans">

      {/* MAIN CARD */}
      <div className="w-full max-w-5xl rounded-md bg-[#FDFDFB] border border-[#E1DFD7] shadow-md md:grid md:grid-cols-2 overflow-hidden">

        {/* LEFT IMAGE & LOGO PANEL */}
        <div className="relative hidden md:flex bg-[#F3ECE1] p-8 overflow-hidden flex-col items-center justify-center border-r border-[#E1DFD7]">
          <div className="w-44 h-24 overflow-hidden relative flex items-center justify-center mb-4">
            <img
              src="/logo_white.webp"
              alt="Mridanga"
              className="absolute w-56 max-w-none h-56 object-contain mix-blend-multiply"
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            />
          </div>
        </div>

        {/* RIGHT FORM PANEL */}
        <div className="flex flex-col justify-center p-8 sm:p-12 bg-[#FDFDFB]">
          <div className="max-w-md mx-auto w-full">

            {/* MODE TOGGLE */}
            {mode !== "otp" && (
              <div className="flex items-center justify-center rounded-sm bg-[#F2F1EC] p-1 mb-6 border border-[#E1DFD7]">
                <button
                  type="button"
                  onClick={() => { setMode("signin"); setError(""); }}
                  className={`h-9 flex-1 text-xs font-semibold uppercase tracking-wider rounded-sm transition-all ${mode === "signin" ? "bg-[#FDFDFB] text-[#2C2A27] shadow-xs" : "text-[#A6A29A] hover:text-[#5A5750]"}`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => { setMode("signup"); setError(""); }}
                  className={`h-9 flex-1 text-xs font-semibold uppercase tracking-wider rounded-sm transition-all ${mode === "signup" ? "bg-[#FDFDFB] text-[#2C2A27] shadow-xs" : "text-[#A6A29A] hover:text-[#5A5750]"}`}
                >
                  Sign Up
                </button>
              </div>
            )}

            <h1 className="text-sm font-semibold text-center text-[#2C2A27] uppercase tracking-widest text-[#9A7B56]">
              {mode === "signin" ? "Welcome Back" : mode === "signup" ? "Create an Account" : "Verify Your Email"}
            </h1>

            {error && (
              <div className="text-xs font-medium text-red-600 bg-red-50 p-2.5 rounded border border-red-100 text-center mt-4">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="text-xs font-medium text-green-600 bg-green-50 p-2.5 rounded border border-green-100 text-center mt-4">
                {successMessage}
              </div>
            )}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>

              {/* OTP VIEW ONLY */}
              {mode === "otp" && (
                <label className="flex flex-col">
                  <p className="text-[11px] font-semibold text-[#5A5750] uppercase tracking-wider mb-2">6-Digit Verification Code</p>
                  <input
                    type="text"
                    maxLength={6}
                    required
                    className="rounded-sm h-11 px-4 bg-white border border-[#E1DFD7] text-[#2C2A27] placeholder-gray-300 text-center tracking-widest text-lg font-mono font-semibold focus:outline-none focus:border-[#9A7B56] transition-colors"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <p className="text-[11px] text-[#8A867E] mt-2 font-light leading-relaxed">
                    We sent a security token to <span className="text-[#2C2A27] font-normal">{email}</span>. Please insert it above to verify your account.
                  </p>
                </label>
              )}

              {/* STANDARD SIGNIN / SIGNUP VIEWS */}
              {mode !== "otp" && (
                <>
                  {mode === "signup" && (
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex flex-col">
                        <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">First Name *</p>
                        <input type="text" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="Rahul" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                      </label>
                      <label className="flex flex-col">
                        <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">Last Name *</p>
                        <input type="text" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="Sharma" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                      </label>
                    </div>
                  )}

                  <label className="flex flex-col">
                    <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">Email Address *</p>
                    <input type="email" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="name@domain.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </label>

                  <label className="flex flex-col">
                    <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">Password *</p>
                    <input type="password" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </label>

                  {mode === "signup" && (
                    <>
                      <label className="flex flex-col">
                        <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">Confirm Password *</p>
                        <input type="password" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="••••••••" value={password2} onChange={(e) => setPassword2(e.target.value)} />
                      </label>
                      <label className="flex flex-col">
                        <p className="text-[11px] font-semibold text-[#5A5750] uppercase mb-1">Mobile Number *</p>
                        <input type="tel" required className="rounded-sm h-10 px-3 bg-white border border-[#E1DFD7] text-xs text-[#2C2A27] placeholder:text-gray-300 focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="9876543210" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                      </label>
                    </>
                  )}
                </>
              )}

              <button
                disabled={loading}
                className="w-full h-11 rounded-sm bg-[#5A5145] hover:bg-[#9A7B56] text-white font-semibold text-xs tracking-widest uppercase transition-colors pt-0.5 mt-2 duration-300"
              >
                {loading ? "Processing..." : mode === "signin" ? "Sign In" : mode === "signup" ? "Get Verification Code" : "Verify & Complete Signup"}
              </button>

              {/* 🌟 CONDITIONAL RESEND OPTION OR TIMER LOCK PANEL */}
              {mode === "otp" && (
                <div className="w-full text-center pt-2">
                  {timer > 0 ? (
                    <p className="text-xs text-[#A6A29A] font-light">
                      Resend code available in <span className="font-semibold text-[#5A5750]">{formatTime(timer)}</span>
                    </p>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading}
                        className="text-xs font-medium text-[#9A7B56] hover:text-[#5A5145] transition-colors underline decoration-dotted"
                      >
                        Resend verification code
                      </button>
                      <span className="hidden sm:inline text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => { setMode("signup"); setOtp(""); }}
                        className="text-xs text-[#A6A29A] hover:text-[#5A5750] transition-colors"
                      >
                        Change details
                      </button>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 